import { makeAutoObservable } from 'mobx'
import { GOFCategory } from '../constants'
import { SystemFieldEnum } from '../constants/fields'

import {
   createFieldResponseModel,
   updateFieldResponseModel
} from '../factories/FieldResponse'
import { createGOFResponseModel } from '../factories/GOFResponse'
import { GOFResponse } from '../types'
import { UnionFieldResponse, UnionFieldResponseModel } from '../types/Field'
import { Collection } from './Collection'
import GOFResponseModel from './GOFResponseModel'
import StageModel from './StageModel'
import TemplateModel from './TemplateModel'

export class GOFResponseDoesNotExistError extends Error {
   constructor(message: string) {
      super(message)
   }
}

export class FieldResponseDoesNotExistError extends Error {
   constructor(message: string) {
      super(message)
   }
}

class RecordModel {
   readonly recordId: string
   template: TemplateModel

   private gofResponsesCollection: Collection<string, GOFResponseModel>
   private fieldResponsesCollection: Collection<string, UnionFieldResponseModel>

   stages: Collection<string, StageModel>

   constructor(
      recordId: string,
      template: TemplateModel,
      stages: StageModel[]
   ) {
      this.recordId = recordId
      this.template = template
      this.stages = new Collection<string, StageModel>(
         'The stage you are trying to access i snot available'
      )

      this.gofResponsesCollection = new Collection<string, GOFResponseModel>(
         'The GOF response you are trying to access is not available.'
      )
      this.fieldResponsesCollection = new Collection<
         string,
         UnionFieldResponseModel
      >('The field response you are trying to access is not available.')

      stages.map(stage => {
         this.stages.addItem(stage.stageId, stage)
      })

      makeAutoObservable(this, {}, { autoBind: true })
   }

   get gofResponses(): GOFResponseModel[] {
      return this.gofResponsesCollection.orderedItems
   }

   get fieldResponses(): UnionFieldResponseModel[] {
      return this.fieldResponsesCollection.orderedItems
   }

   //#region GOFResponse

   get systemGOFResponses() {
      return this.gofResponses.filter(
         gofResponse => gofResponse.gof.gofCategory === GOFCategory.SystemGOF
      )
   }

   get customGOFResponses() {
      return this.gofResponses.filter(
         gofResponse => gofResponse.gof.gofCategory === GOFCategory.CustomGOF
      )
   }

   createOrUpdateGOFResponse = (gofResponse: GOFResponse) => {
      const { gofId, gofResponseId } = gofResponse

      this.template.createOrUpdateGOF({ gofId, orderedFieldIds: [] })

      const gof = this.template.getGOF(gofId)

      if (!this.hasGOFResponse(gofResponseId)) {
         const gofResponseModel = createGOFResponseModel(
            gofResponseId,
            gof,
            this
         )
         this.gofResponsesCollection.addItem(
            gofResponseModel.gofResponseId,
            gofResponseModel
         )
      }

      // NOTE: there won't be any update case for the gof response model
   }
   getGOFResponse(gofResponseId: string): GOFResponseModel {
      return this.gofResponsesCollection.getItem(gofResponseId)
   }

   getGOFResponseWithGOFId = (gofId: string): GOFResponseModel | undefined => {
      const gofResponse = this.gofResponses.find(
         gofResponse => gofResponse.gof.gofId === gofId
      )

      return gofResponse
   }
   hasGOFResponse = (gofResponseId: string): boolean =>
      this.gofResponsesCollection.hasItem(gofResponseId)
   deleteGOFResponse(gofResponseId: string) {
      const hasGOFResponse = this.hasGOFResponse(gofResponseId)

      if (!hasGOFResponse) {
         throw new GOFResponseDoesNotExistError(
            'The GOFResponse you are trying to access is not available.'
         )
      }

      this.gofResponsesCollection.deleteItem(gofResponseId)
   }
   //#endregion

   //#region FieldResponse

   createOrUpdateFieldResponse = (
      gofId: string,
      gofResponseId: string,
      fieldResponse: UnionFieldResponse
   ) => {
      const { fieldId, fieldResponseId, fieldType } = fieldResponse

      if (!this.hasGOFResponse(gofResponseId)) {
         this.createOrUpdateGOFResponse({
            gofResponseId: gofResponseId,
            gofId: gofId
         })
      }

      const gofResponseModel = this.getGOFResponse(gofResponseId)

      if (!this.hasFieldResponse(fieldResponseId)) {
         if (!this.template.hasField(fieldId)) {
            this.template.createOrUpdateField(gofId, {
               fieldId,
               fieldType
            })
         }

         const fieldModel = this.template.getField(fieldId)

         // NOTE: This is a cleanup method to remove the Dummy Field Response Model from the field responses collection and gof response collection.
         this._removeDummyFieldResponseModel(
            fieldId,
            fieldResponseId,
            gofResponseId
         )
         const fieldResponseModel = createFieldResponseModel(
            fieldResponse,
            fieldModel,
            gofResponseModel
         )

         gofResponseModel.fieldResponsesCollection.addItem(
            fieldResponseModel.fieldResponseId,
            fieldResponseModel
         )

         this.fieldResponsesCollection.addItem(
            fieldResponseId,
            fieldResponseModel
         )
      }

      const fieldResponseModel = this.getFieldResponse(fieldResponseId)

      if (gofResponseId !== fieldResponseModel.gofResponse?.gofResponseId) {
         const existingGofResponseModel = fieldResponseModel.gofResponse
         existingGofResponseModel?.fieldResponsesCollection.deleteItem(
            fieldResponseId
         )

         fieldResponseModel.setGOFResponse(gofResponseModel)
         gofResponseModel.fieldResponsesCollection.addItem(
            fieldResponseId,
            fieldResponseModel
         )
      }

      updateFieldResponseModel(fieldResponseModel, fieldResponse)
   }

   _removeDummyFieldResponseModel = (
      fieldId: string,
      currentFieldResponseId: string,
      gofResponseId: string
   ): void => {
      const fieldResponseModel = this.fieldResponses.find(
         field =>
            field.field.fieldId === fieldId &&
            field.fieldResponseId !== currentFieldResponseId
      )

      if (fieldResponseModel) {
         this.fieldResponsesCollection.deleteItem(
            fieldResponseModel.fieldResponseId
         )
      }

      const gofResponseModel = this.getGOFResponse(gofResponseId)

      const hasResponseInGofResponse = gofResponseModel.fieldResponses.find(
         field =>
            field.field.fieldId === fieldId &&
            field.fieldResponseId !== currentFieldResponseId
      )

      if (hasResponseInGofResponse) {
         gofResponseModel.fieldResponsesCollection.deleteItem(
            fieldResponseModel?.fieldResponseId ?? ''
         )
      }
   }

   getFieldResponse(fieldResponseId: string): UnionFieldResponseModel {
      return this.fieldResponsesCollection.getItem(fieldResponseId)
   }

   getAddAnotherGOFFieldResponse = (
      gofResponseId: string,
      fieldId: string
   ): UnionFieldResponseModel => {
      const gofResponse = this.getGOFResponse(gofResponseId)
      return gofResponse.getFieldResponseWithFieldId(
         fieldId
      ) as UnionFieldResponseModel
   }

   getFieldResponseModelWithSystemFieldEnum(
      systemFieldEnum: SystemFieldEnum
   ): UnionFieldResponseModel | undefined {
      return this.fieldResponses.find(
         fieldResponse =>
            fieldResponse.field.systemFieldEnum === systemFieldEnum
      )
   }
   hasFieldResponse = (fieldResponseId: string): boolean =>
      this.fieldResponsesCollection.hasItem(fieldResponseId)
   deleteFieldResponse(fieldResponseId: string) {
      if (!this.hasFieldResponse(fieldResponseId)) {
         throw new FieldResponseDoesNotExistError(
            `${fieldResponseId} : The Field Response you are trying to access is not available.`
         )
      }

      const fieldResponseModel = this.getFieldResponse(fieldResponseId)

      fieldResponseModel.gofResponse?.fieldResponsesCollection.deleteItem(
         fieldResponseId
      )
      this.fieldResponsesCollection.deleteItem(fieldResponseId)
   }

   clearGOFResponse = (gofResponseId: string): void => {
      const gofResponseModel = this.getGOFResponse(gofResponseId)
      if (!gofResponseModel) return

      gofResponseModel.fieldResponsesCollection.clearData()
   }

   //#endregion
}

export default RecordModel
